import { Link } from "@inertiajs/react"
import { Folder, ImageOff, Loader2, Tag } from "lucide-react"
import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"
import { ClipActionsMenu } from "./ClipActionsMenu"
import { ClipStatusBadge } from "./ClipStatusBadge"
import { ClipTagPills } from "./ClipTagPills"
import { OneTimeUseBadge } from "./OneTimeUseBadge"
import { useThumbnailHoverCycle } from "./useThumbnailHoverCycle"
import type { Clip } from "./types"

export function ClipCard({
  clip,
  selected,
  pendingThumbnail,
  onSelectChange,
  onRequestThumbnails,
}: {
  clip: Clip
  selected: boolean
  pendingThumbnail?: boolean
  onSelectChange: (clipId: number, selected: boolean) => void
  onRequestThumbnails?: (ids: number[]) => void
}) {
  const { ref, index } = useThumbnailHoverCycle<HTMLElement>(clip.thumbnails)
  const displayedThumb = clip.thumbnails[index] ?? clip.thumbnails[0]
  const url = displayedThumb?.url ?? clip.primary_thumbnail_url

  return (
    <article
      ref={ref}
      className={cn(
        "group relative overflow-hidden rounded-lg border bg-surface transition-colors",
        selected ? "border-accent ring-2 ring-accent-faded" : "border-hairline hover:border-accent",
      )}
    >
      <label className="absolute left-2 top-2 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded-md bg-page/90 shadow-sm">
        <Checkbox
          checked={selected}
          onChange={(e) => onSelectChange(clip.id, e.target.checked)}
          aria-label={`Select ${clip.title ?? clip.current_filename}`}
        />
      </label>

      <div className="absolute right-2 top-2 z-10">
        <ClipActionsMenu
          clip={clip}
          tone="overlay"
          onRequestThumbnails={onRequestThumbnails ? (id) => onRequestThumbnails([id]) : undefined}
        />
      </div>

      <Link
        href={`/clips/${clip.id}`}
        className="block aspect-video w-full overflow-hidden bg-page no-underline"
      >
        {pendingThumbnail && !url ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-ink-muted">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="text-xs">Fetching thumbnail…</span>
          </div>
        ) : url ? (
          <img
            src={url}
            alt=""
            className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-ink-muted">
            <ImageOff className="h-6 w-6" />
            <span className="text-xs">No thumbnails yet</span>
          </div>
        )}
      </Link>

      <div className="space-y-1 p-3">
        <Link
          href={`/clips/${clip.id}`}
          className="block truncate font-medium text-ink-display no-underline hover:text-accent-display"
          title={clip.title ?? clip.current_filename ?? ""}
        >
          {clip.title || clip.current_filename || "Untitled"}
        </Link>
        {clip.short_description && (
          <p className="line-clamp-2 text-xs text-ink-muted">{clip.short_description}</p>
        )}
        {clip.tag_names.length > 0 && (
          <div className="flex items-center gap-1.5 pt-0.5">
            <Tag className="h-3 w-3 shrink-0 text-ink-muted" />
            <ClipTagPills tags={clip.tag_names} />
          </div>
        )}
        <div className="flex items-center justify-between gap-2 text-[11px] text-ink-muted">
          <span className="inline-flex min-w-0 items-center gap-1.5 truncate" title={clip.current_folder_path ?? ""}>
            <Folder className="h-3 w-3 shrink-0" />
            <span className="truncate">{clip.current_folder_path || "/"}</span>
          </span>
          {clip.duration_seconds != null && (
            <span className="shrink-0">{formatDuration(clip.duration_seconds)}</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1 pt-1">
          <ClipStatusBadge status={clip.status} />
          {clip.one_time_use && <OneTimeUseBadge />}
        </div>
        {clip.status === "needs_processing" && onRequestThumbnails && (
          <button
            type="button"
            onClick={() => onRequestThumbnails([clip.id])}
            className="block cursor-pointer text-[10px] text-accent-display underline-offset-2 hover:underline"
          >
            Get thumbnail &amp; enrich
          </button>
        )}
      </div>
    </article>
  )
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, "0")}`
}
